import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { procedureOptions, timeframeOptions } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    country: '',
    procedure: '',
    reportsAvailable: '',
    timeframe: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.phone || !formData.country || !formData.procedure) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to backend
      const response = await axios.post(`${API}/enquiry`, formData);
      
      console.log('Form submitted successfully:', response.data);
      
      if (response.data.synced_to_sheets) {
        toast.success('Thank you! Your enquiry has been submitted and saved to our records. We will contact you within 24 hours.');
      } else {
        toast.success('Thank you! Your enquiry has been received. We will contact you within 24 hours.');
      }
      
      // Reset form
      setFormData({
        name: '',
        phone: '',
        city: '',
        country: '',
        procedure: '',
        reportsAvailable: '',
        timeframe: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit enquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="enquiry" className="section-padding" style={{ background: 'var(--bg-section)' }}>
      <div className="container">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="heading-1 mb-4">Start Your Healthcare Journey</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Fill out the form below and our team will get back to you within 24 hours
            </p>
          </div>

          <div 
            className="voice-card accent-grey"
            style={{ padding: '2.5rem' }}
          >
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg body-medium"
                    style={{
                      border: '1px solid var(--border-input)',
                      background: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      outline: 'none',
                      transition: 'border 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg body-medium"
                    style={{
                      border: '1px solid var(--border-input)',
                      background: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      outline: 'none',
                      transition: 'border 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg body-medium"
                    style={{
                      border: '1px solid var(--border-input)',
                      background: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      outline: 'none',
                      transition: 'border 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                    placeholder="Your city"
                  />
                </div>

                <div>
                  <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                    Country *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg body-medium"
                    style={{
                      border: '1px solid var(--border-input)',
                      background: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      outline: 'none',
                      transition: 'border 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                    placeholder="Your country"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                    Procedure Type *
                  </label>
                  <select
                    name="procedure"
                    value={formData.procedure}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg body-medium"
                    style={{
                      border: '1px solid var(--border-input)',
                      background: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      outline: 'none',
                      transition: 'border 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                  >
                    <option value="">Select procedure</option>
                    {procedureOptions.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                    Medical Reports Available?
                  </label>
                  <select
                    name="reportsAvailable"
                    value={formData.reportsAvailable}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg body-medium"
                    style={{
                      border: '1px solid var(--border-input)',
                      background: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      outline: 'none',
                      transition: 'border 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                  >
                    <option value="">Select option</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                  Expected Timeframe
                </label>
                <select
                  name="timeframe"
                  value={formData.timeframe}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg body-medium"
                  style={{
                    border: '1px solid var(--border-input)',
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    transition: 'border 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                >
                  <option value="">Select timeframe</option>
                  {timeframeOptions.map((option, index) => (
                    <option key={index} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <button 
                type="submit" 
                className="btn-primary w-full flex items-center justify-center gap-2"
                disabled={isSubmitting}
                style={{
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnquiryForm;